/**
 * Shared chart kit — palette, formatters, tooltip and the ShareList used in place of donuts.
 *
 * Colour rules (dataviz method):
 *  - categorical hues come from one validated 8-slot palette, assigned in FIXED order and never
 *    cycled; colour follows the entity (Cat 4 is always slot 1), never its rank;
 *  - light and dark are separately selected steps of the same eight hues;
 *  - text never wears the series colour — values and labels stay in text tokens.
 */
import React from 'react';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';

// Reference palette (dataviz references/palette.md), light and dark steps.
const SERIES = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
};

/** Fixed slot per GHG category and transport mode, so the same entity is the same colour everywhere. */
export const CATEGORY_SLOT = { 4: 0, 6: 1, 7: 2, 9: 3 };
export const MODE_SLOT = { road: 0, rail: 2, air: 1, water: 3, sea: 3 };

function useReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = React.useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

export function usePalette() {
  const { resolvedTheme } = useTheme();
  const reducedMotion = useReducedMotion();
  const dark = resolvedTheme === 'dark';
  const series = dark ? SERIES.dark : SERIES.light;
  return {
    dark,
    series,
    /** pass to Recharts `isAnimationActive`: no grow-in animation for reduced-motion users */
    animate: !reducedMotion,
    /** colour for a fixed slot index */
    slot: (i) => series[i % series.length],
    category: (cat) => series[CATEGORY_SLOT[cat] ?? 0],
    mode: (m) => series[MODE_SLOT[String(m || '').toLowerCase()] ?? 0],
    /** one hue for ranked single-measure bars */
    single: series[0],
  };
}

// ── formatting ────────────────────────────────────────────────────────────────────────────

const nf1 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
const nf0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

/** 110169.6 → "110,169.6" */
export const fmtNumber = (n, digits = 1) =>
  Number.isFinite(Number(n))
    ? (digits === 0 ? nf0 : digits === 2 ? nf2 : nf1).format(Number(n))
    : '—';

/** kg CO2e for headline tiles: tonnes once ≥ 1,000 kg. Returns { value, unit }. */
export function emissionsParts(kg) {
  const v = Number(kg) || 0;
  if (Math.abs(v) >= 1000) return { value: nf1.format(v / 1000), unit: 't CO₂e' };
  return { value: nf1.format(v), unit: 'kg CO₂e' };
}

export const fmtKg = (kg) => `${fmtNumber(kg)} kg CO₂e`;

/** share of a whole: tiny non-zero shares read "<0.1%" instead of a misleading "0%" */
export function fmtShare(part, whole) {
  const w = Number(whole) || 0;
  if (!w) return '—';
  const pct = (Number(part) / w) * 100;
  if (pct > 0 && pct < 0.1) return '<0.1%';
  return `${pct >= 10 ? nf0.format(pct) : nf1.format(pct)}%`;
}

/** compact axis ticks: 12500 → "12.5k" */
export const fmtAxis = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  if (Math.abs(v) >= 1e6) return `${nf1.format(v / 1e6)}M`;
  if (Math.abs(v) >= 1e3) return `${nf1.format(v / 1e3)}k`;
  return nf0.format(v);
};

// ── tooltip ───────────────────────────────────────────────────────────────────────────────

/**
 * Token-styled Recharts tooltip. `rows(payload)` returns [{ label, value, color? }].
 * Identity is carried by a colour mark beside the label; the text stays in text tokens.
 */
export function ChartTooltip({ active, payload, label, title, rows }) {
  if (!active || !payload || !payload.length) return null;
  const items = rows
    ? rows(payload)
    : payload.map((p) => ({ label: p.name, value: p.value, color: p.color || p.fill }));
  const heading = title ? title(payload, label) : label;
  return (
    <div className="min-w-[180px] rounded-lg border border-border bg-popover px-3 py-2.5 text-xs text-popover-foreground shadow-lg">
      {heading != null && heading !== '' && (
        <p className="mb-1.5 font-semibold text-foreground">{heading}</p>
      )}
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              {it.color && <span className="h-2 w-2 rounded-sm" style={{ background: it.color }} aria-hidden="true" />}
              {it.label}
            </span>
            <span className="tabular-nums font-medium text-foreground">{it.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── ShareList: part-to-whole without a donut ──────────────────────────────────────────────

/**
 * Ranked part-to-whole list: every part gets its own row with name, value and share, and a bar
 * scaled to its share. Replaces donuts, whose small slices could not be labelled without
 * overlapping (one 99% slice + several <1% slices).
 *
 * items: [{ key, name, value, color? }]   — `color` identifies the entity (optional)
 */
export function ShareList({ items, total, unit = 'kg CO₂e', formatValue = (v) => fmtNumber(v), className, emptyText = 'No data yet.' }) {
  const rows = [...(items || [])].filter((i) => Number(i.value) > 0).sort((a, b) => b.value - a.value);
  const whole = total ?? rows.reduce((s, r) => s + Number(r.value), 0);
  const max = rows.length ? Number(rows[0].value) : 0;

  if (!rows.length) {
    return <p className={cn('py-6 text-center text-sm text-muted-foreground', className)}>{emptyText}</p>;
  }

  return (
    <ul className={cn('space-y-3.5', className)} aria-label="Share of total">
      {rows.map((r) => {
        const widthPct = max > 0 ? Math.max(0.6, (Number(r.value) / max) * 100) : 0;
        return (
          <li key={r.key ?? r.name} title={`${r.name}: ${formatValue(r.value)} ${unit} (${fmtShare(r.value, whole)})`}>
            {/* Wraps the figures onto their own line on narrow screens rather than truncating the name */}
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-sm">
              <span className="flex min-w-[10rem] flex-1 items-center gap-2 text-foreground">
                {r.color && <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: r.color }} aria-hidden="true" />}
                <span>{r.name}</span>
              </span>
              <span className="ml-auto shrink-0 tabular-nums text-foreground">
                <span className="font-semibold">{formatValue(r.value)}</span>
                <span className="text-muted-foreground"> {unit}</span>
                <span className="ml-2 inline-block w-12 text-right text-muted-foreground">{fmtShare(r.value, whole)}</span>
              </span>
            </div>
            <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
              <div
                className="h-full rounded-full transition-[width] duration-500 ease-out"
                style={{ width: `${widthPct}%`, background: r.color || 'hsl(var(--primary))' }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** Consistent chart card header: title + one-line description, no decorative gradient badges. */
export function ChartHeader({ title, description, right }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 sm:px-6 sm:pt-6">
      <div className="min-w-0">
        <h3 className="text-[15px] font-semibold leading-tight text-foreground">{title}</h3>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {right}
    </div>
  );
}

/** Shared axis props: recessive ticks, no axis line, compact numbers. */
export const axisProps = {
  tickLine: false,
  axisLine: false,
  fontSize: 12,
  tickMargin: 8,
};
